import { inject, type ComputedRef, type Ref } from 'vue'
import { type AxiosInstance } from 'axios'

import type { User, AppsDBEntry as GeneratedAppsDbEntry } from '@openapi'
import type { PitcherInfo } from '@lib/types/app'

type AppsDBEntry = GeneratedAppsDbEntry & { value: Record<string, any> | null }

interface EntryParams {
  store_name: string
  user_id: User['id']
  entry_key: string
}

interface GlobalEntryParams {
  store_name: string
  entry_key: string
}

interface UpsertEntryParams extends EntryParams {
  data: Record<string, any>
}

interface UpsertGlobalEntryParams extends GlobalEntryParams {
  data: Record<string, any>
}

const BASE_PATH = `appsdb/stores/`

type UseAppsDbPayload = {
  pitcherInfo?: ComputedRef<PitcherInfo>
  restApiAxios?: Ref<AxiosInstance | undefined>
}

export function useAppsDb(payload: UseAppsDbPayload = {}) {
  const pitcherInfo = payload.pitcherInfo ?? inject<ComputedRef<PitcherInfo>>('pitcherInfo')
  if (!pitcherInfo?.value) throw new Error('Pitcher info is not provided')
  const restApiAxios = payload.restApiAxios ?? inject<Ref<AxiosInstance | undefined>>('restApiAxios')
  if (!restApiAxios?.value) throw new Error('REST API axios was not provided')

  const upsertEntry = async (payload: UpsertEntryParams) => {
    try {
      if (!pitcherInfo?.value || !restApiAxios.value) throw new Error('Pitcher info or axios instance is not provided')
      const { store_name, user_id, entry_key, data } = payload
      const response = await restApiAxios.value.put<unknown, AppsDBEntry>(
        `${BASE_PATH}${store_name}/instances/${pitcherInfo.value.instanceId}/users/${user_id}/entries/${entry_key}/`,
        { value: data },
      )
      return response
    } catch (error) {
      console.error('Error upserting entry:', error)
      throw error
    }
  }

  const getEntry = async (params: EntryParams) => {
    try {
      if (!pitcherInfo?.value || !restApiAxios.value) throw new Error('Pitcher info or axios instance is not provided')
      const { store_name, user_id, entry_key } = params
      const response = await restApiAxios.value.get<unknown, AppsDBEntry & { value: Record<string, any> }>(
        `${BASE_PATH}${store_name}/instances/${pitcherInfo.value.instanceId}/users/${user_id}/entries/${entry_key}/`,
      )
      return response
    } catch (error) {
      console.error('Error getting entry:', error)
      throw error
    }
  }

  const deleteEntry = async (params: EntryParams) => {
    try {
      if (!pitcherInfo?.value || !restApiAxios.value) throw new Error('Pitcher info or axios instance is not provided')
      const { store_name, user_id, entry_key } = params
      const response = await restApiAxios.value.delete<void>(
        `${BASE_PATH}${store_name}/instances/${pitcherInfo.value.instanceId}/users/${user_id}/entries/${entry_key}/`,
      )
      return response
    } catch (error) {
      console.error('Error deleting entry:', error)
      throw error
    }
  }

  const upsertGlobalEntry = async (payload: UpsertGlobalEntryParams) => {
    try {
      if (!pitcherInfo?.value || !restApiAxios.value) throw new Error('Pitcher info or axios instance is not provided')
      const { store_name, entry_key, data } = payload
      const response = await restApiAxios.value.put<unknown, AppsDBEntry>(
        `${BASE_PATH}${store_name}/instances/${pitcherInfo.value.instanceId}/globals/entries/${entry_key}/`,
        { value: data },
      )
      return response
    } catch (error) {
      console.error('Error upserting global entry:', error)
      throw error
    }
  }

  const getGlobalEntry = async (params: GlobalEntryParams) => {
    try {
      if (!pitcherInfo?.value || !restApiAxios.value) throw new Error('Pitcher info or axios instance is not provided')
      const { store_name, entry_key } = params
      const response = await restApiAxios.value.get<unknown, AppsDBEntry>(
        `${BASE_PATH}${store_name}/instances/${pitcherInfo.value.instanceId}/globals/entries/${entry_key}/`,
      )
      return response
    } catch (error) {
      console.error('Error getting global entry:', error)
      throw error
    }
  }

  const deleteGlobalEntry = async (params: GlobalEntryParams) => {
    try {
      if (!pitcherInfo?.value || !restApiAxios.value) throw new Error('Pitcher info or axios instance is not provided')
      const { store_name, entry_key } = params
      const response = await restApiAxios.value.delete<void>(
        `${BASE_PATH}${store_name}/instances/${pitcherInfo.value.instanceId}/globals/entries/${entry_key}/`,
      )
      return response
    } catch (error) {
      console.error('Error deleting global entry:', error)
      throw error
    }
  }

  return {
    upsertEntry,
    getEntry,
    deleteEntry,
    upsertGlobalEntry,
    getGlobalEntry,
    deleteGlobalEntry,
  }
}
